import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CompanyCombobox from "@/components/CompanyCombobox";
import api, { formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";

const DEPARTMENTS = ["Engineering", "Product", "Sales", "Support", "Operations", "Marketing", "HR", "Finance", "Design"];

export default function AddSupervisorModal({ open, onOpenChange, onCreated, initialName = "" }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", title: "", company: "", department: "", location: "" });

  useEffect(() => {
    if (open) setForm({ name: initialName || "", title: "", company: "", department: "", location: "" });
  }, [open, initialName]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.company.trim()) { setError("Please select or add a company."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/supervisors", form);
      toast.success(`${data.name} added`);
      onOpenChange(false);
      setForm({ name: "", title: "", company: "", department: "", location: "" });
      if (onCreated) onCreated(data);
      navigate(`/supervisor/${data.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="add-supervisor-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl flex items-center gap-2" style={{ fontWeight: 800 }}>
            <UserPlus className="text-emerald-600" /> Add a Supervisor
          </DialogTitle>
        </DialogHeader>

        {error && (
          <div data-testid="add-supervisor-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4 mt-1">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Full name</Label>
              <Input data-testid="add-supervisor-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" />
            </div>
            <div>
              <Label>Job title</Label>
              <Input data-testid="add-supervisor-title-input" required value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Engineering Manager" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Company</Label>
              <CompanyCombobox value={form.company} onChange={(v) => set("company", v)} testid="add-supervisor-company" />
            </div>
            <div>
              <Label>Department</Label>
              <Select value={form.department} onValueChange={(v) => set("department", v)} required>
                <SelectTrigger data-testid="add-supervisor-department-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Location (optional)</Label>
            <Input data-testid="add-supervisor-location-input" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City, State or Remote" />
          </div>
          <Button data-testid="add-supervisor-form-submit" type="submit" disabled={loading || !form.department} className="w-full bg-slate-900 hover:bg-emerald-600 rounded-full">
            {loading ? "Adding..." : "Add & Open Profile"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
